import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VideoBrief } from "@/components/VideoBrief";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ListChecks, Bell, Check, RotateCcw, Clock, MessageSquare } from "lucide-react";

function dueLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00"); const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const txt = d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  if (diff < 0) return { txt, tone: "text-rose-600" };
  if (diff === 0) return { txt: "Today", tone: "text-[#d19100]" };
  if (diff <= 3) return { txt, tone: "text-[#d19100]" };
  return { txt, tone: "text-[#9FA1A4]" };
}
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`;
}

function ClientTaskDialog({ taskId, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const { data } = await api.get(`/ws/tasks/${taskId}`); setT(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onClose(); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);
  const addComment = async () => {
    if (!comment.trim()) return; setBusy(true);
    try { const { data } = await api.post(`/ws/tasks/${taskId}/comment`, { text: comment.trim() }); setT(data); setComment(""); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggleDone = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/ws/tasks/${taskId}/complete`, { reopen: t.is_completed }); setT(data); onChanged && onChanged(); toast.success(t.is_completed ? "Reopened" : "Marked done"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] max-w-lg" data-testid="client-task-dialog">
        {!t ? <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div> : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{t.code}</span>
                {t.project_name && <span className="text-[10px] rounded-full bg-[#F6F6FD] border border-[#EEEFEF] px-2 py-0.5 text-[#595D62]">{t.project_name}</span>}
                {t.is_completed && <span className="text-[10px] rounded-full bg-[#E7F6EC] text-[#166534] px-2 py-0.5 font-medium">Done</span>}
              </div>
              <h3 className="font-display text-lg font-semibold text-[#06060C]" data-testid="client-task-title">{t.title}</h3>
              {t.due_date && (() => { const d = dueLabel(t.due_date); return <p className={`text-xs mt-1 flex items-center gap-1 ${d.tone}`}><Clock className="h-3 w-3" /> Due {d.txt}</p>; })()}
            </div>
            <div className="border-t border-[#f0f0f3] pt-3"><VideoBrief base={`/ws/tasks/${taskId}`} /></div>
            {t.description && <p className="text-sm text-[#595D62] whitespace-pre-wrap border-t border-[#f0f0f3] pt-3">{t.description}</p>}
            <div className="border-t border-[#f0f0f3] pt-3">
              <p className="text-xs font-semibold text-[#06060C] mb-2 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Comments</p>
              <div className="space-y-2 max-h-52 overflow-y-auto mb-3">
                {(t.comments || []).length === 0 && <p className="text-xs text-[#9FA1A4]">No comments yet.</p>}
                {(t.comments || []).map((c, i) => (
                  <div key={i} className="rounded-xl bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2" data-testid="client-task-comment">
                    <div className="flex items-center justify-between"><span className="text-xs font-medium text-[#06060C]">{c.actor || "Someone"}</span><span className="text-[10px] text-[#9FA1A4]">{timeAgo(c.at)}</span></div>
                    <p className="text-sm text-[#595D62] whitespace-pre-wrap mt-0.5">{c.comment}</p>
                  </div>
                ))}
              </div>
              <Textarea data-testid="client-task-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Add a comment…" className="bg-[#F6F6FD] border-[#EEEFEF] text-sm resize-none" />
              <div className="flex items-center justify-between mt-3">
                <Button data-testid="client-task-comment-send" size="sm" variant="outline" onClick={addComment} disabled={busy || !comment.trim()} className="border-[#EEEFEF] bg-white rounded-full">Comment</Button>
                <Button data-testid="client-task-toggle-done" size="sm" onClick={toggleDone} disabled={busy} className={t.is_completed ? "bg-white border border-[#EEEFEF] text-[#595D62] hover:bg-[#F6F6FD] rounded-full" : "bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t.is_completed ? <><RotateCcw className="h-4 w-4 mr-1.5" /> Reopen</> : <><Check className="h-4 w-4 mr-1.5" /> Mark done</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ClientInbox() {
  const [data, setData] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const load = async () => { try { const { data } = await api.get("/inbox"); setData(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);
  const markRead = async (n) => { if (!n.read) { await api.post(`/inbox/notifications/${n.id}/read`).catch(() => {}); } if (typeof n.link === "string" && n.link.startsWith("task:")) setOpenTask(n.link.slice(5)); if (typeof n.link === "string" && n.link.startsWith("social:")) { window.location.assign(`${window.location.pathname.split("/").slice(0, 2).join("/")}/social/${n.link.slice(7)}`); return; } if (typeof n.link === "string" && n.link.startsWith("doc:")) { window.location.assign(`${window.location.pathname.split("/").slice(0, 2).join("/")}/docs/${n.link.slice(4)}`); return; } load(); };
  const readAll = async () => { await api.post("/inbox/notifications/read-all").catch(() => {}); load(); };

  if (!data) return <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>;
  const c = data.counts || {};
  return (
    <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
      <p className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#723FFF] mb-2.5">Inbox</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Your inbox</h1>
      <p className="mt-3 text-sm text-[#9FA1A4] mb-8">Tasks assigned to you and your notifications, all in one place.</p>

      <Tabs defaultValue="tasks">
        <TabsList className="bg-[#F6F6FD] border border-[#EEEFEF] rounded-full p-1">
          <TabsTrigger value="tasks" data-testid="inbox-tab-tasks" className="rounded-full data-[state=active]:bg-white text-sm gap-1.5"><ListChecks className="h-4 w-4" /> My Tasks{c.my_tasks ? <span className="ml-1 rounded-full bg-[#EDE7FF] text-[#5b2fd6] text-[10px] font-semibold px-1.5">{c.my_tasks}</span> : null}</TabsTrigger>
          <TabsTrigger value="notifs" data-testid="inbox-tab-notifs" className="rounded-full data-[state=active]:bg-white text-sm gap-1.5"><Bell className="h-4 w-4" /> Notifications{c.unread ? <span className="ml-1 rounded-full bg-rose-100 text-rose-600 text-[10px] font-semibold px-1.5">{c.unread}</span> : null}</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-6">
          {data.my_tasks.length === 0 ? <p className="text-sm text-[#9FA1A4] py-10 text-center">No tasks assigned to you right now.</p> : (
            <div className="space-y-2" data-testid="inbox-my-tasks">
              {data.my_tasks.map((t) => { const d = dueLabel(t.due_date); return (
                <button key={t.id} data-testid={`inbox-task-${t.id}`} onClick={() => setOpenTask(t.id)} className="w-full text-left rounded-2xl border border-[#EEEFEF] bg-white hover:bg-[#F6F6FD] transition-colors px-4 py-3 flex items-center gap-3">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${t.is_completed ? "bg-[#22c55e]" : "bg-[#723FFF]"}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2"><span className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{t.code}</span>{t.project_name && <span className="text-[10px] rounded-full bg-[#F6F6FD] border border-[#EEEFEF] px-2 py-0.5 text-[#595D62]">{t.project_name}</span>}</span>
                    <span className={`block text-sm font-medium truncate ${t.is_completed ? "text-[#9FA1A4] line-through" : "text-[#06060C]"}`}>{t.title}</span>
                  </span>
                  {d && <span className={`text-xs shrink-0 ${d.tone}`}>{d.txt}</span>}
                </button>
              ); })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notifs" className="mt-6">
          <div className="flex justify-end mb-3">{c.unread ? <Button data-testid="inbox-read-all" size="sm" variant="outline" onClick={readAll} className="border-[#EEEFEF] bg-white rounded-full text-xs">Mark all read</Button> : null}</div>
          {data.notifications.length === 0 ? <p className="text-sm text-[#9FA1A4] py-10 text-center">No notifications yet.</p> : (
            <div className="space-y-2" data-testid="inbox-notifs">
              {data.notifications.map((n) => (
                <button key={n.id} data-testid={`inbox-notif-${n.id}`} onClick={() => markRead(n)} className={`w-full text-left rounded-2xl border px-4 py-3 flex items-start gap-3 transition-colors ${n.read ? "border-[#EEEFEF] bg-white hover:bg-[#F6F6FD]" : "border-[#d9ccff] bg-[#F7F4FF] hover:bg-[#EDE7FF]"}`}>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-[#723FFF] mt-1.5 shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-[#06060C]">{n.title}</span>
                    {n.body && <span className="block text-xs text-[#595D62] mt-0.5">{n.body}</span>}
                  </span>
                  <span className="text-[10px] text-[#9FA1A4] shrink-0">{timeAgo(n.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {openTask && <ClientTaskDialog taskId={openTask} onClose={() => setOpenTask(null)} onChanged={load} />}
    </main>
  );
}
